import React, { useEffect, useState, createContext, useContext } from 'react';
import { useChat } from './ChatContext';
import { useRecording } from './RecordingContext';
import { useClosingAssistant } from './ClosingAssistantContext';
import { usePresentationMode } from './PresentationModeContext';
type ActiveFeature = 'chat' | 'audio' | 'closing' | 'insights';
interface AISalesAssistantContextType {
  isOpen: boolean;
  isMinimized: boolean;
  activeFeature: ActiveFeature;
  hasUnreadInsights: boolean;
  hasImportantInsights: boolean;
  readinessScore: number;
  closingReadiness: 'low' | 'medium' | 'high';
  recentTranscript: string;
  setIsOpen: (isOpen: boolean) => void;
  setIsMinimized: (isMinimized: boolean) => void;
  setActiveFeature: (feature: ActiveFeature) => void;
  toggleAssistant: () => void;
  getUnreadCount: () => number;
}
const AISalesAssistantContext = createContext<AISalesAssistantContextType | undefined>(undefined);
export const AISalesAssistantProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const {
    isPresentationMode
  } = usePresentationMode();
  const {
    messages,
    isOpen: chatIsOpen,
    setIsOpen: setChatIsOpen
  } = useChat();
  const {
    recordingState,
    transcript
  } = useRecording();
  const {
    isEnabled: closingIsEnabled,
    isMinimized: closingIsMinimized,
    insights,
    customerProfile,
    closingReadiness,
    toggleMinimized: toggleClosingMinimized
  } = useClosingAssistant();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [activeFeature, setActiveFeature] = useState<ActiveFeature>('insights');
  const [hasUnreadInsights, setHasUnreadInsights] = useState(false);
  const [hasImportantInsights, setHasImportantInsights] = useState(false);
  const [recentTranscript, setRecentTranscript] = useState('');
  // Synchronize with chat state
  useEffect(() => {
    if (chatIsOpen && !isOpen) {
      setIsOpen(true);
      setActiveFeature('chat');
    }
  }, [chatIsOpen, isOpen]);
  // Handle transcript updates
  useEffect(() => {
    if (transcript && transcript !== recentTranscript) {
      setRecentTranscript(transcript);
      // If the transcript contains certain keywords, update the active feature
      const lowerTranscript = transcript.toLowerCase();
      if (lowerTranscript.includes('price') || lowerTranscript.includes('cost')) {
        setActiveFeature('closing');
      }
    }
  }, [transcript, recentTranscript]);
  // Check for unread insights
  useEffect(() => {
    const unreadInsights = insights.filter(insight => !insight.dismissed);
    setHasUnreadInsights(unreadInsights.length > 0);
    const importantInsights = unreadInsights.filter(insight => insight.severity === 'success' || insight.severity === 'error');
    setHasImportantInsights(importantInsights.length > 0);
    // If there's an important insight and the assistant is minimized, show a notification
    if (importantInsights.length > 0 && isMinimized) {
      // We'll handle this with the NotificationBadge component
    }
  }, [insights, isMinimized]);
  // Disable in presentation mode
  useEffect(() => {
    if (isPresentationMode) {
      setIsOpen(false);
    }
  }, [isPresentationMode]);
  // Toggle the assistant open/closed state
  const toggleAssistant = () => {
    if (isMinimized) {
      setIsMinimized(false);
    } else if (isOpen) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
    // Sync with chat state
    if (!isOpen) {
      setChatIsOpen(true);
    } else {
      setChatIsOpen(false);
    }
  };
  // Get count of unread messages
  const getUnreadCount = () => {
    if (!isOpen) {
      const aiMessages = messages.filter(msg => msg.sender === 'ai');
      const unreadInsightsCount = insights.filter(insight => !insight.dismissed).length;
      return aiMessages.length + unreadInsightsCount;
    }
    return 0;
  };
  return <AISalesAssistantContext.Provider value={{
    isOpen,
    isMinimized,
    activeFeature,
    hasUnreadInsights,
    hasImportantInsights,
    readinessScore: customerProfile.readinessScore,
    closingReadiness,
    recentTranscript,
    setIsOpen,
    setIsMinimized,
    setActiveFeature,
    toggleAssistant,
    getUnreadCount
  }} data-id="element-4281">
      {children}
    </AISalesAssistantContext.Provider>;
};
export const useAISalesAssistant = () => {
  const context = useContext(AISalesAssistantContext);
  if (context === undefined) {
    throw new Error('useAISalesAssistant must be used within an AISalesAssistantProvider');
  }
  return context;
};